import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios defaults
axios.defaults.withCredentials = true;

// ---- Global 401 → refresh → retry interceptor (set up exactly once) ----
let interceptorInstalled = false;
let refreshPromise = null;
function installAuthInterceptor(onAuthLost) {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;
      const url = original?.url || '';

      // Only handle 401s, never retry the refresh call itself, never retry twice
      const isRefreshCall = url.includes('/api/auth/refresh');
      const isLoginCall = url.includes('/api/auth/login') || url.includes('/api/auth/register');
      if (status !== 401 || !original || original._retried || isRefreshCall || isLoginCall) {
        return Promise.reject(error);
      }
      original._retried = true;

      try {
        // Dedupe concurrent refreshes
        if (!refreshPromise) {
          refreshPromise = axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
            .finally(() => { refreshPromise = null; });
        }
        await refreshPromise;
        return axios(original);
      } catch (refreshErr) {
        if (typeof onAuthLost === 'function') onAuthLost();
        return Promise.reject(error);
      }
    }
  );
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);
  const userRef = useRef(user);
  userRef.current = user;

  // Install global 401-refresh-retry interceptor once
  useEffect(() => {
    installAuthInterceptor(() => {
      // refresh failed → session is truly gone
      setUser(false);
    });
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(data);
      return data;
    } catch (error) {
      // Try to refresh token
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setUser(data);
        return data;
      } catch {
        setUser(false);
        return null;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, user: data };
    } catch (error) {
      return { 
        success: false, 
        error: formatApiErrorDetail(error.response?.data?.detail) || error.message 
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
